/**
 * How an organisation's VAT verification READS (#2267 port 2).
 *
 * Presentation only, and pure, so the one property that has never been true can
 * be asserted without a browser: **a verification the server refused is not
 * presented as a verification.**
 *
 * ── what the legacy badge did ───────────────────────────────────────────────
 * `VatBadge` in `crm/RecordPage.tsx` rendered `rec.vat_check`, and its Verify
 * button called `onChecked(result)`, which the page turned into
 * `void save.mutateAsync({ id, patch: { vat_check: result } })`.
 *
 * Three things follow, and the third is the one that matters:
 *
 *   · the write was `void`ed, so a refusal was never awaited and never shown;
 *   · the button was offered to a caller with no write authority, whose save
 *     the server then refused — the editable-then-refused shape `RecordBody`
 *     was explicitly ruled against;
 *   · **`core.organization` has no `vat_check` column.** MEASURED: 0022 creates
 *     the table with `legal_name · display_name · kind · registration_number ·
 *     vat_number · vat_country · jurisdiction · website · attributes ·
 *     is_golden`, later migrations add `source_ref` (0063) and
 *     `counterparty_type` (2026081707), and nothing anywhere adds `vat_check` —
 *     not a column, not a `register_field_meta` row, not even a fixture key.
 *
 * So every verification this product has ever performed was written to a column
 * that does not exist, refused with 42703, swallowed by the `void`, and held in
 * React state until the page reloaded. The capability has never once persisted
 * anything, and what it claims to persist is a VIES consultation number — the
 * evidence for charging no VAT on a cross-border sale.
 *
 * ── and the gap has a second end ────────────────────────────────────────────
 * `billing.vat_treatment` takes `p_vat_id_validated` and fails CLOSED on it
 * (billing 0022), because `@solum/vat` refuses reverse charge on a
 * claimed-but-unvalidated id. Nothing in the fleet supplies that boolean. The
 * console's badge was the surface that looked like it did.
 *
 * This module therefore does NOT choose a store. Where reverse-charge evidence
 * lives is a platform decision — a column on `core.organization`, a typed row a
 * billing module owns, or a key in `attributes` — with a tier decision attached
 * (rule 36) and consequences for `vat_treatment`'s parameter. It is raised on
 * #2267 rather than answered here.
 */
import type { Tone } from '@solum/ui'
import type { VatResult } from './companyLookup'

export type VatVerificationPhase = 'stored' | 'checking' | 'unsaved' | 'refused'

export interface VatVerificationView {
  /** Present unless there is nothing to say at all. */
  tone: Tone
  label: string
  /** The registry's own words about the check — never invented here. */
  note?: string
  /** VIES consultation number: the reverse-charge evidence, when there is one. */
  consultation?: string
  /** The registry's name for the holder, where VIES returned one. */
  name?: string
  /**
   * The sentence about STORAGE. Empty when the result is the stored one.
   *
   * This is the field the legacy surface had no equivalent of, and its absence
   * is what made a never-persisted check indistinguishable from a persisted one.
   */
  persistence?: string
}

const TONE: Record<VatResult['status'], Tone> = {
  valid: 'good', format_valid: 'brand', invalid: 'critical', format_invalid: 'critical', unavailable: 'warn',
}
const LABEL: Record<VatResult['status'], string> = {
  valid: 'VIES valid', format_valid: 'format valid', invalid: 'VIES: invalid',
  format_invalid: 'format invalid', unavailable: 'not checked',
}

/**
 * What the badge says, given the verification and what became of the write.
 *
 * `refusal` is the SERVER's own message, passed through verbatim. It is not
 * re-worded: a person who can act on "column vat_check does not exist" is an
 * operator, and paraphrasing it into "could not save" removes the only part
 * that says what to fix.
 */
export function vatVerificationView(
  result: VatResult | undefined, phase: VatVerificationPhase, refusal?: string,
): VatVerificationView | null {
  if (!result) return null
  const base: VatVerificationView = {
    tone: TONE[result.status] ?? 'neutral',
    label: LABEL[result.status] ?? result.status,
    note: result.note,
    consultation: result.consultation,
    name: result.name,
  }
  if (phase === 'stored') return base
  if (phase === 'checking') return { ...base, persistence: 'Checking…' }
  if (phase === 'unsaved') return { ...base, persistence: 'Recorded on this record.' }
  return {
    ...base,
    /* The check RAN and its answer stands; what failed is recording it. Saying
       "verification failed" here would be a different and wrong claim, and it is
       the one a reader makes when a refusal is folded into the status pill. */
    tone: 'warn',
    persistence: refusal
      ? `Checked, but this deployment could not record the result — ${refusal}`
      : 'Checked, but this deployment could not record the result.',
  }
}

/**
 * Is this result evidence, or a floor?
 *
 * `checkVat` falls back to the offline format rules when VIES is unreachable —
 * which in a browser is the ORDINARY case, because VIES sends no CORS headers,
 * and `companyLookup.ts` says so in its own fallback note. A `format_valid`
 * result is a syntax check, and a syntax check is not what `@solum/vat` means
 * by a validated id.
 */
export const isRegistryEvidence = (result: VatResult | undefined): boolean =>
  result?.status === 'valid' && !!result.consultation
